import { useEffect, useMemo, useRef, useState } from "react";
import { useProjectLibraryStore } from "../store/project-library-store";
import { useSkinStore } from "../store/skinStore";
import { cx } from "../styles/cx";
import ui from "../styles/ui.module.css";
import styles from "./AppHeader.module.css";
import { Icon } from "./ui/Icon";

type ProjectSwitcherProps = {
  onNotify: (message: string, tone?: "success" | "error") => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ProjectSwitcher({
  onNotify,
  open,
  onOpenChange,
}: ProjectSwitcherProps) {
  const projects = useProjectLibraryStore((state) => state.projects);
  const activeProjectId = useProjectLibraryStore(
    (state) => state.activeProjectId,
  );
  const busy = useProjectLibraryStore((state) => state.busy);
  const ready = useProjectLibraryStore((state) => state.ready);
  const create = useProjectLibraryStore((state) => state.create);
  const switchTo = useProjectLibraryStore((state) => state.switchTo);
  const rename = useProjectLibraryStore((state) => state.rename);
  const remove = useProjectLibraryStore((state) => state.remove);
  const isAiRunning = useSkinStore((state) => state.isAiRunning);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!renamingId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renamingId]);

  const active = useMemo(
    () => projects.find((project) => project.id === activeProjectId) ?? null,
    [projects, activeProjectId],
  );

  const disabled = busy || isAiRunning || !ready;

  const handleToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    onOpenChange(event.currentTarget.open);
  };

  const beginRename = (id: string, name: string) => {
    setRenamingId(id);
    setRenameValue(name);
  };

  const commitRename = async () => {
    if (!renamingId) return;
    const next = renameValue.trim();
    if (!next) {
      setRenamingId(null);
      return;
    }
    try {
      await rename(renamingId, next);
      onNotify(`已重命名为「${next}」`);
    } catch (error) {
      onNotify(error instanceof Error ? error.message : "重命名失败", "error");
    } finally {
      setRenamingId(null);
    }
  };

  return (
    <details
      className={cx(styles.newMenu, styles.projectSwitcher)}
      data-topbar-menu=""
      open={open}
      onToggle={handleToggle}
    >
      <summary
        className={cx(
          ui.button,
          styles.topbarButton,
          styles.projectSwitcherSummary,
        )}
        aria-label="切换项目"
        title={active?.name ?? "项目"}
      >
        <Icon name="folder" size={16} />
        <span className={styles.projectSwitcherName}>
          {active?.name ?? (ready ? "项目" : "加载中…")}
        </span>
        <span className={styles.projectSwitcherCaret} aria-hidden="true">
          ▾
        </span>
      </summary>
      <div className={cx(styles.newMenuPopover, styles.projectSwitcherPopover)}>
        <div className={styles.projectSwitcherHeading}>本机项目</div>
        <ul className={styles.projectSwitcherList}>
          {projects.map((project) => {
            const isActive = project.id === activeProjectId;
            const isRenaming = renamingId === project.id;
            return (
              <li
                className={cx(
                  styles.projectSwitcherItem,
                  isActive && styles.isActive,
                )}
                key={project.id}
              >
                {isRenaming ? (
                  <form
                    className={styles.projectRenameForm}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void commitRename();
                    }}
                  >
                    <input
                      aria-label="项目名称"
                      className={styles.projectRenameInput}
                      onBlur={() => void commitRename()}
                      onChange={(event) => setRenameValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          event.preventDefault();
                          setRenamingId(null);
                        }
                      }}
                      ref={renameInputRef}
                      value={renameValue}
                    />
                  </form>
                ) : (
                  <>
                    <button
                      className={styles.projectSwitcherSelect}
                      disabled={disabled || isActive}
                      onClick={() => {
                        void switchTo(project.id)
                          .then(() => {
                            onOpenChange(false);
                            onNotify(`已切换到「${project.name}」`);
                          })
                          .catch((error: unknown) => {
                            onNotify(
                              error instanceof Error
                                ? error.message
                                : "切换失败",
                              "error",
                            );
                          });
                      }}
                      type="button"
                    >
                      <strong>{project.name}</strong>
                      <small>
                        {isActive ? "当前" : formatUpdatedAt(project.updatedAt)}
                      </small>
                    </button>
                    <div className={styles.projectSwitcherItemActions}>
                      <button
                        aria-label={`重命名 ${project.name}`}
                        className={styles.projectIconButton}
                        disabled={disabled}
                        onClick={() => beginRename(project.id, project.name)}
                        type="button"
                      >
                        改
                      </button>
                      <button
                        aria-label={`删除 ${project.name}`}
                        className={cx(styles.projectIconButton, styles.danger)}
                        disabled={disabled || projects.length <= 1}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `确定删除项目「${project.name}」？此操作不可撤销。`,
                            )
                          ) {
                            return;
                          }
                          void remove(project.id)
                            .then(() => onNotify(`已删除「${project.name}」`))
                            .catch((error: unknown) => {
                              onNotify(
                                error instanceof Error
                                  ? error.message
                                  : "删除失败",
                                "error",
                              );
                            });
                        }}
                        type="button"
                      >
                        删
                      </button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        <div className={styles.projectSwitcherFooter}>
          <button
            disabled={disabled}
            onClick={() => {
              void create({ seed: "defaultClassic", name: undefined })
                .then((created) => {
                  onOpenChange(false);
                  onNotify(`已创建「${created.name}」`);
                })
                .catch((error: unknown) => {
                  onNotify(
                    error instanceof Error ? error.message : "创建失败",
                    "error",
                  );
                });
            }}
            type="button"
          >
            <span>
              <strong>新建空白项目</strong>
              <small>从默认 Steve 皮肤开始</small>
            </span>
          </button>
          <button
            disabled={disabled}
            onClick={() => {
              void create({ seed: "fromCurrent" })
                .then((created) => {
                  onOpenChange(false);
                  onNotify(`已复制为「${created.name}」`);
                })
                .catch((error: unknown) => {
                  onNotify(
                    error instanceof Error ? error.message : "复制失败",
                    "error",
                  );
                });
            }}
            type="button"
          >
            <span>
              <strong>复制当前项目</strong>
              <small>另存为新项目并切换过去</small>
            </span>
          </button>
        </div>
      </div>
    </details>
  );
}

function formatUpdatedAt(timestamp: number): string {
  try {
    return new Date(timestamp).toLocaleString("zh-CN", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
